// components/admpelada.js
import React, { useEffect, useMemo, useState } from "react";
import { addMinutes } from "date-fns";
import api from "../api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./admpelada.css";

/* Traduções */
const tSessionStatus = (s) => {
  switch (s) {
    case "open": return "Aberta";
    case "closed": return "Fechada";
    case "canceled": return "Cancelada";
    case "finished": return "Finalizada";
    default: return s || "—";
  }
};
const tGroupStatus = (s) => {
  switch (s) {
    case "active": return "Ativo";
    case "inactive": return "Inativo";
    default: return s || "—";
  }
};

/* UI: Chips e Barra */
const Pill = ({ label, value, max, tone = "neutral" }) => {
  const over = max > 0 && value > max;
  const cls = [
    "pill",
    tone === "green" && "pill--green",
    tone === "amber" && "pill--amber",
    tone === "red" && "pill--red",
    over && "pill--over",
  ].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      <span className="pill__label">{label}</span>
      <span className="pill__value">
        {value}{max ? `/${max}` : ""}
      </span>
    </div>
  );
};
const Progress = ({ value, max }) => {
  const pct = Math.max(0, Math.min(100, max ? Math.round((value / max) * 100) : 0));
  return (
    <div className="progress">
      <div className="progress__bar" style={{ width: `${pct}%` }} />
    </div>
  );
};

const AdminPelada = ({ peladaId: groupId, onStartLoading, onFinishLoading }) => {
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` }),
    []
  );

  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [counts, setCounts] = useState({ line: 0, goalie: 0, wait: 0 });
  const [editSession, setEditSession] = useState(false);
  const [editGroup, setEditGroup] = useState(false);

  // ---- ASAAS (API key por grupo) ----
  const [asaasStatus, setAsaasStatus] = useState({ configured: false, masked: null });
  const [asaasKeyInput, setAsaasKeyInput] = useState("");
  const [asaasPixKeyInput, setAsaasPixKeyInput] = useState("");
  const [asaasCourtPixKeyInput, setAsaasCourtPixKeyInput] = useState("");
  const [asaasBusy, setAsaasBusy] = useState(false);
  const [asaasMsg, setAsaasMsg] = useState(null); // { type: 'ok'|'error', text }
  const [asaasSummary, setAsaasSummary] = useState(null); // { balance, transactions }
  const [asaasSummaryLoading, setAsaasSummaryLoading] = useState(false);
  const [asaasSummaryError, setAsaasSummaryError] = useState(null);

  // ---- FORM SESSION (schema sessions) ----
  const [formSession, setFormSession] = useState({
    scheduled_at: null,
    place_name: "",
    place_address: "",
    status: "open",
  });

  // ---- FORM GROUP (schema groups) ----
  const [formGroup, setFormGroup] = useState({
    name: "",
    status: "active",
    weekday: "",
    time_of_day: "",
    place_name: "",
    place_address: "",
    max_players: 0,
    max_goalkeepers: 0,
    max_players_line: 0,
    max_waiting: 0,
    list_open: true,
    fee_amount: "",
    monthly_amount: "",
    pix_key: "",
    extra_slots_enabled: false,
    extra_slots_max: 0,
  });

  const parseIntSafe = (v, d = 0) => {
    const n = parseInt(String(v ?? "").replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? n : d;
  };

  // ---- LOADERS ----
  const loadGroup = async () => {
    const { data } = await api.get(`/api/groups/${groupId}`, { headers });
    setFormGroup({
      name: data.name || "",
      status: data.status || "active",
      weekday: data.weekday || "",
      time_of_day: data.time_of_day || "",
      place_name: data.place_name || "",
      place_address: data.place_address || "",
      max_players: parseIntSafe(data.max_players, 0),
      max_goalkeepers: parseIntSafe(data.max_goalkeepers, 0),
      max_players_line: parseIntSafe(data.max_players_line, 0),
      max_waiting: parseIntSafe(data.max_waiting, 0),
      list_open: !!data.list_open,
      fee_amount: data.fee_amount ?? "",
      monthly_amount: data.monthly_amount ?? "",
      pix_key: data.pix_key ?? "",
      extra_slots_enabled: !!data.extra_slots_enabled,
      extra_slots_max: parseIntSafe(data.extra_slots_max, 0),
    });
  };

  const loadCounts = async (sessionId) => {
    try {
      const { data } = await api.get(`/api/sessions/${sessionId}/attendances`, { headers });
      const line = Array.isArray(data?.confirmados) ? data.confirmados.length : (data?.counts?.confirmados || 0);
      const goalie = Array.isArray(data?.goleirosConfirmados) ? data.goleirosConfirmados.length : (data?.counts?.goleirosConfirmados || 0);
      const wait = Array.isArray(data?.listaEspera) ? data.listaEspera.length : (data?.counts?.listaEspera || 0);
      setCounts({ line, goalie, wait });
    } catch (e) {
      console.warn("Falha ao ler presenças:", e?.response?.data || e.message);
    }
  };

  const loadNextSession = async () => {
    const { data } = await api.get(`/api/groups/${groupId}/next-session`, { headers });
    if (data) {
      setSession(data);
      setFormSession({
        scheduled_at: data.scheduled_at ? new Date(data.scheduled_at) : null,
        place_name: data.place_name || "",
        place_address: data.place_address || "",
        status: data.status || "open",
      });
      await loadCounts(data.id);
    } else {
      const now = addMinutes(new Date(), 60);
      setSession(null);
      setCounts({ line: 0, goalie: 0, wait: 0 });
      setFormSession({ scheduled_at: now, place_name: "", place_address: "", status: "open" });
    }
  };

  const loadAsaasStatus = async () => {
    try {
      const { data } = await api.get(`/api/groups/${groupId}/asaas-key`, { headers });
      setAsaasStatus({ configured: !!data?.configured, masked: data?.masked || null });
      setAsaasPixKeyInput(data?.pixKey || "");
      setAsaasCourtPixKeyInput(data?.courtPixKey || "");
    } catch (e) {
      console.warn("Falha ao ler status da API key do Asaas:", e?.response?.data || e.message);
    }
  };

  const loadAsaasSummary = async () => {
    setAsaasSummaryLoading(true);
    setAsaasSummaryError(null);
    try {
      const { data } = await api.get(`/api/groups/${groupId}/asaas-key/summary`, { headers });
      setAsaasSummary(data);
    } catch (e) {
      setAsaasSummaryError(e.response?.data?.error || "Erro ao consultar saldo/transações.");
    } finally {
      setAsaasSummaryLoading(false);
    }
  };

  const bootstrap = async () => {
    onStartLoading?.();
    try {
      await Promise.all([loadGroup(), loadNextSession(), loadAsaasStatus()]);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao carregar dados.");
    } finally {
      onFinishLoading?.();
    }
  };

  useEffect(() => { if (groupId) bootstrap(); /* eslint-disable-next-line */ }, [groupId]);

  // ---- SAVERS ----
  const saveSession = async () => {
    onStartLoading?.();
    try {
      const payload = {
        scheduled_at: formSession.scheduled_at?.toISOString() || null,
        place_name: formSession.place_name || null,
        place_address: formSession.place_address || null,
        status: formSession.status || "open",
      };
      if (session?.id) {
        await api.put(`/api/sessions/${session.id}`, payload, { headers });
      } else {
        const { data: created } = await api.post(`/api/groups/${groupId}/sessions`, payload, { headers });
        setSession(created);
      }
      await loadNextSession();
      setEditSession(false);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar a sessão.");
    } finally {
      onFinishLoading?.();
    }
  };

  const saveGroup = async () => {
    onStartLoading?.();
    try {
      const body = {
        name: formGroup.name,
        status: formGroup.status,
        weekday: formGroup.weekday,
        time_of_day: formGroup.time_of_day,
        place_name: formGroup.place_name,
        place_address: formGroup.place_address,
        max_players: parseIntSafe(formGroup.max_players, 0),
        max_goalkeepers: parseIntSafe(formGroup.max_goalkeepers, 0),
        max_players_line: parseIntSafe(formGroup.max_players_line, 0),
        max_waiting: parseIntSafe(formGroup.max_waiting, 0),
        list_open: !!formGroup.list_open,
        fee_amount: formGroup.fee_amount,
        monthly_amount: formGroup.monthly_amount,
        pix_key: formGroup.pix_key,
        extra_slots_enabled: !!formGroup.extra_slots_enabled,
        extra_slots_max: parseIntSafe(formGroup.extra_slots_max, 0),
      };
      await api.put(`/api/groups/${groupId}`, body, { headers });
      await loadGroup();
      setEditGroup(false);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar dados do grupo.");
    } finally {
      onFinishLoading?.();
    }
  };

  const saveAsaasKey = async () => {
    const key = asaasKeyInput.trim();
    if (!key) {
      setAsaasMsg({ type: "error", text: "Informe a API key do Asaas." });
      return;
    }
    setAsaasBusy(true);
    setAsaasMsg(null);
    try {
      const body = { apiKey: key, pixKey: asaasPixKeyInput.trim() };
      const { data } = await api.put(`/api/groups/${groupId}/asaas-key`, body, { headers });
      setAsaasStatus({ configured: !!data?.configured, masked: data?.masked || null });
      setAsaasKeyInput("");
      setAsaasMsg({ type: "ok", text: "API key validada e salva com sucesso." });
    } catch (err) {
      setAsaasMsg({ type: "error", text: err.response?.data?.error || "Erro ao salvar a API key." });
    } finally {
      setAsaasBusy(false);
    }
  };

  const savePixKey = async () => {
    setAsaasBusy(true);
    setAsaasMsg(null);
    try {
      const { data } = await api.put(
        `/api/groups/${groupId}/asaas-key/pix-key`,
        { pixKey: asaasPixKeyInput.trim() },
        { headers }
      );
      setAsaasPixKeyInput(data?.pixKey || "");
      setAsaasMsg({ type: "ok", text: data?.pixKey ? "Chave Pix salva com sucesso." : "Filtro de chave Pix removido." });
    } catch (err) {
      setAsaasMsg({ type: "error", text: err.response?.data?.error || "Erro ao salvar a chave Pix." });
    } finally {
      setAsaasBusy(false);
    }
  };

  const saveCourtPixKey = async () => {
    setAsaasBusy(true);
    setAsaasMsg(null);
    try {
      const { data } = await api.put(
        `/api/groups/${groupId}/asaas-key/court-pix-key`,
        { courtPixKey: asaasCourtPixKeyInput.trim() },
        { headers }
      );
      setAsaasCourtPixKeyInput(data?.courtPixKey || "");
      setAsaasMsg({ type: "ok", text: data?.courtPixKey ? "Chave Pix da quadra salva com sucesso." : "Chave Pix da quadra removida." });
    } catch (err) {
      setAsaasMsg({ type: "error", text: err.response?.data?.error || "Erro ao salvar a chave Pix da quadra." });
    } finally {
      setAsaasBusy(false);
    }
  };

  const removeAsaasKey = async () => {
    if (!window.confirm("Remover a API key do Asaas deste grupo? Os PIX recebidos deixarão de ser identificados automaticamente.")) {
      return;
    }
    setAsaasBusy(true);
    setAsaasMsg(null);
    try {
      await api.delete(`/api/groups/${groupId}/asaas-key`, { headers });
      setAsaasStatus({ configured: false, masked: null });
      setAsaasMsg({ type: "ok", text: "API key removida." });
    } catch (err) {
      setAsaasMsg({ type: "error", text: err.response?.data?.error || "Erro ao remover a API key." });
    } finally {
      setAsaasBusy(false);
    }
  };

  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="admin-screen">
      <header className="admin-header">
        <h1 className="title">Administração do Jogo</h1>
        <div className="header-actions">
          <button className="btn btn--ghost" onClick={() => session?.id && loadCounts(session.id)} disabled={!session?.id}>
            Atualizar Presenças
          </button>
        </div>
      </header>

      {/* Sessão */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Próxima Sessão</h2>
          <button className="btn" onClick={() => setEditSession(v => !v)}>
            {editSession ? "Cancelar" : "Editar"}
          </button>
        </div>

        {editSession ? (
          <div className="form-grid">
            <div className="field">
              <label>Data e Hora</label>
              <DatePicker
                selected={formSession.scheduled_at}
                onChange={(date) => setFormSession((p) => ({ ...p, scheduled_at: date }))}
                showTimeSelect
                timeIntervals={15}
                dateFormat="dd/MM/yyyy HH:mm"
                placeholderText="Selecione data e hora"
                className="input"
              />
            </div>

            <div className="field">
              <label>Status (sessão)</label>
              <select
                className="input"
                value={formSession.status}
                onChange={(e) => setFormSession((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="open">Aberta</option>
                <option value="closed">Fechada</option>
                <option value="canceled">Cancelada</option>
                <option value="finished">Finalizada</option>
              </select>
            </div>

            <div className="field">
              <label>Local</label>
              <input
                className="input"
                value={formSession.place_name}
                onChange={(e) => setFormSession((p) => ({ ...p, place_name: e.target.value }))}
                placeholder="Nome da quadra/campo"
              />
            </div>

            <div className="field">
              <label>Endereço</label>
              <input
                className="input"
                value={formSession.place_address}
                onChange={(e) => setFormSession((p) => ({ ...p, place_address: e.target.value }))}
                placeholder="Endereço completo"
              />
            </div>

            <div className="form-actions">
              <button className="btn btn--primary" onClick={saveSession}>Salvar Sessão</button>
            </div>
          </div>
        ) : (
          <div className="read-grid">
            <div className="read-item">
              <span className="read-label">Data e Hora</span>
              <span className="read-value">
                {formSession.scheduled_at ? formSession.scheduled_at.toLocaleString("pt-BR") : "Não definida"}
              </span>
            </div>
            <div className="read-item">
              <span className="read-label">Status</span>
              <span className={`badge badge--${formSession.status}`}>{tSessionStatus(formSession.status)}</span>
            </div>
            <div className="read-item">
              <span className="read-label">Local</span>
              <span className="read-value">{formSession.place_name || "—"}</span>
            </div>
            <div className="read-item">
              <span className="read-label">Endereço</span>
              <span className="read-value">{formSession.place_address || "—"}</span>
            </div>
          </div>
        )}
      </section>

      {/* Presenças */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Presenças</h2>
        </div>

        <div className="pill-row">
          <Pill label="Linha" value={counts.line} max={formGroup.max_players_line} tone="green" />
          <Pill label="Goleiros" value={counts.goalie} max={formGroup.max_goalkeepers} tone="amber" />
          <Pill label="Fila de Espera" value={counts.wait} max={formGroup.max_waiting} tone="red" />
        </div>

        <div className="progress-grid">
          <div>
            <div className="progress-title">Linha</div>
            <Progress value={counts.line} max={formGroup.max_players_line || 0} />
          </div>
          <div>
            <div className="progress-title">Goleiros</div>
            <Progress value={counts.goalie} max={formGroup.max_goalkeepers || 0} />
          </div>
          <div>
            <div className="progress-title">Fila de Espera</div>
            <Progress value={counts.wait} max={formGroup.max_waiting || 0} />
          </div>
        </div>

        <small className="muted">
          Limites vêm do grupo. Ajuste-os abaixo conforme necessário.
        </small>
      </section>

      {/* Grupo */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Configurações do Grupo</h2>
          <button className="btn" onClick={() => setEditGroup(v => !v)}>
            {editGroup ? "Cancelar" : "Editar"}
          </button>
        </div>

        {editGroup ? (
          <div className="form-grid">
            <div className="field">
              <label>Nome</label>
              <input className="input" value={formGroup.name} onChange={(e)=>setFormGroup(p=>({...p, name:e.target.value}))} />
            </div>
            <div className="field">
              <label>Status</label>
              <select className="input" value={formGroup.status} onChange={(e)=>setFormGroup(p=>({...p, status:e.target.value}))}>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </select>
            </div>
            <div className="field">
              <label>Dia da semana</label>
              <input className="input" value={formGroup.weekday} onChange={(e)=>setFormGroup(p=>({...p, weekday:e.target.value}))} placeholder="domingo/segunda/..." />
            </div>
            <div className="field">
              <label>Horário (HH:mm:ss)</label>
              <input className="input" value={formGroup.time_of_day} onChange={(e)=>setFormGroup(p=>({...p, time_of_day:e.target.value}))} placeholder="20:00:00" />
            </div>
            <div className="field">
              <label>Local padrão</label>
              <input className="input" value={formGroup.place_name} onChange={(e)=>setFormGroup(p=>({...p, place_name:e.target.value}))} />
            </div>
            <div className="field">
              <label>Endereço padrão</label>
              <input className="input" value={formGroup.place_address} onChange={(e)=>setFormGroup(p=>({...p, place_address:e.target.value}))} />
            </div>

            <div className="divider" />

            <div className="field">
              <label>Máx. Linha</label>
              <input type="number" min={0} className="input" value={formGroup.max_players_line}
                     onChange={(e)=>setFormGroup(p=>({...p, max_players_line: parseIntSafe(e.target.value)}))}/>
            </div>
            <div className="field">
              <label>Máx. Goleiros</label>
              <input type="number" min={0} className="input" value={formGroup.max_goalkeepers}
                     onChange={(e)=>setFormGroup(p=>({...p, max_goalkeepers: parseIntSafe(e.target.value)}))}/>
            </div>
            <div className="field">
              <label>Máx. Fila de Espera</label>
              <input type="number" min={0} className="input" value={formGroup.max_waiting}
                     onChange={(e)=>setFormGroup(p=>({...p, max_waiting: parseIntSafe(e.target.value)}))}/>
            </div>

            <div className="divider" />

            <div className="field">
              <label>Taxa por jogo (BRL)</label>
              <input className="input" value={formGroup.fee_amount} onChange={(e)=>setFormGroup(p=>({...p, fee_amount: e.target.value}))} placeholder="18.00" />
            </div>
            <div className="field">
              <label>Mensalidade (BRL)</label>
              <input className="input" value={formGroup.monthly_amount} onChange={(e)=>setFormGroup(p=>({...p, monthly_amount: e.target.value}))} placeholder="60.00" />
            </div>
            <div className="field">
              <label>Chave PIX</label>
              <input className="input" value={formGroup.pix_key} onChange={(e)=>setFormGroup(p=>({...p, pix_key: e.target.value}))} placeholder="chave pix" />
            </div>

            <div className="field field--row">
              <label className="label-inline">
                <input type="checkbox" checked={!!formGroup.list_open} onChange={(e)=>setFormGroup(p=>({...p, list_open: e.target.checked}))}/>
                Lista aberta por padrão
              </label>
            </div>

            <div className="field field--row">
              <label className="label-inline">
                <input type="checkbox" checked={!!formGroup.extra_slots_enabled} onChange={(e)=>setFormGroup(p=>({...p, extra_slots_enabled: e.target.checked}))}/>
                Habilitar slots extras
              </label>
              <input type="number" min={0} className="input input--xs"
                     value={formGroup.extra_slots_max}
                     onChange={(e)=>setFormGroup(p=>({...p, extra_slots_max: parseIntSafe(e.target.value)}))}/>
            </div>

            <div className="form-actions">
              <button className="btn btn--primary" onClick={saveGroup}>Salvar Configurações do Grupo</button>
            </div>
          </div>
        ) : (
          <div className="read-grid">
            <div className="read-item"><span className="read-label">Nome</span><span className="read-value">{formGroup.name}</span></div>
            <div className="read-item"><span className="read-label">Status</span><span className="read-value">{tGroupStatus(formGroup.status)}</span></div>
            <div className="read-item"><span className="read-label">Dia/Hora</span><span className="read-value">{formGroup.weekday} • {formGroup.time_of_day}</span></div>
            <div className="read-item"><span className="read-label">Local</span><span className="read-value">{formGroup.place_name || "—"}</span></div>
            <div className="read-item"><span className="read-label">Endereço</span><span className="read-value">{formGroup.place_address || "—"}</span></div>
            <div className="read-item"><span className="read-label">Máx. Linha</span><span className="read-value">{formGroup.max_players_line}</span></div>
            <div className="read-item"><span className="read-label">Máx. Goleiros</span><span className="read-value">{formGroup.max_goalkeepers}</span></div>
            <div className="read-item"><span className="read-label">Máx. Espera</span><span className="read-value">{formGroup.max_waiting}</span></div>
            <div className="read-item"><span className="read-label">Lista aberta</span><span className="read-value">{formGroup.list_open ? "Sim" : "Não"}</span></div>
            <div className="read-item"><span className="read-label">Taxa/Mensal</span><span className="read-value">{formGroup.fee_amount} / {formGroup.monthly_amount}</span></div>
            <div className="read-item"><span className="read-label">PIX</span><span className="read-value">{formGroup.pix_key || "—"}</span></div>
            <div className="read-item"><span className="read-label">Slots Extras</span><span className="read-value">{formGroup.extra_slots_enabled ? `Sim (${formGroup.extra_slots_max})` : "Não"}</span></div>
          </div>
        )}
      </section>

      {/* Integração Asaas */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Integração Asaas</h2>
        </div>

        <div className="read-grid">
          <div className="read-item">
            <span className="read-label">Status</span>
            <span className="read-value">
              {asaasStatus.configured ? `Configurada (${asaasStatus.masked})` : "Não configurada"}
            </span>
          </div>
        </div>

        {asaasMsg && (
          <p className={asaasMsg.type === "error" ? "error-message" : "success-message"}>
            {asaasMsg.text}
          </p>
        )}

        <div className="form-grid">
          <div className="field">
            <label>API key do Asaas</label>
            <input
              type="password"
              className="input"
              value={asaasKeyInput}
              onChange={(e) => setAsaasKeyInput(e.target.value)}
              placeholder={asaasStatus.configured ? "Digite uma nova key para substituir" : "$aact_..."}
              autoComplete="off"
            />
          </div>
          <div className="field">
            <label>Chave Pix exclusiva desta pelada (opcional)</label>
            <input
              type="text"
              className="input"
              value={asaasPixKeyInput}
              onChange={(e) => setAsaasPixKeyInput(e.target.value)}
              placeholder="ex.: +5532987034969 — deixe em branco se a conta só tem 1 chave"
              autoComplete="off"
            />
            <span className="muted">
              Se a mesma conta Asaas tiver mais de uma chave Pix cadastrada, preencha aqui pra só processar os Pix recebidos por essa chave (evita misturar movimentação pessoal com a da pelada).
            </span>
            {asaasStatus.configured && (
              <button className="btn btn--ghost" onClick={savePixKey} disabled={asaasBusy} style={{ marginTop: 8 }}>
                Salvar chave Pix
              </button>
            )}
          </div>
          <div className="field">
            <label>Chave Pix da quadra (opcional)</label>
            <input
              type="text"
              className="input"
              value={asaasCourtPixKeyInput}
              onChange={(e) => setAsaasCourtPixKeyInput(e.target.value)}
              placeholder="ex.: chave Pix de quem recebe o aluguel da quadra"
              autoComplete="off"
            />
            <span className="muted">
              Quando uma transferência sair dessa conta Asaas pra essa chave, o bot avisa no grupo automaticamente.
            </span>
            {asaasStatus.configured && (
              <button className="btn btn--ghost" onClick={saveCourtPixKey} disabled={asaasBusy} style={{ marginTop: 8 }}>
                Salvar chave da quadra
              </button>
            )}
          </div>
          <div className="form-actions">
            <button className="btn btn--primary" onClick={saveAsaasKey} disabled={asaasBusy}>
              {asaasBusy ? "Validando..." : "Salvar API key"}
            </button>
            {asaasStatus.configured && (
              <button className="btn btn--ghost" onClick={removeAsaasKey} disabled={asaasBusy}>
                Remover
              </button>
            )}
          </div>
        </div>

        {asaasStatus.configured && (
          <div className="form-grid" style={{ marginTop: 16 }}>
            <div className="form-actions">
              <button className="btn" onClick={loadAsaasSummary} disabled={asaasSummaryLoading}>
                {asaasSummaryLoading ? "Consultando..." : "Ver saldo e últimas transações"}
              </button>
            </div>

            {asaasSummaryError && <p className="error-message">{asaasSummaryError}</p>}

            {asaasSummary && (
              <div>
                <div className="read-item">
                  <span className="read-label">Saldo atual</span>
                  <span className="read-value">
                    R$ {Number(asaasSummary.balance ?? 0).toFixed(2)}
                  </span>
                </div>

                <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse", fontSize: 13 }}>
                  <tbody>
                    {(asaasSummary.transactions || []).map((t) => (
                      <tr key={t.id} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                        <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>{t.date}</td>
                        <td style={{ padding: "6px 4px" }}>{t.description}</td>
                        <td
                          style={{
                            padding: "6px 4px",
                            textAlign: "right",
                            whiteSpace: "nowrap",
                            color: Number(t.value) < 0 ? "#fca5a5" : "#86efac",
                          }}
                        >
                          {Number(t.value) < 0 ? "-" : "+"}R$ {Math.abs(Number(t.value)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {(!asaasSummary.transactions || asaasSummary.transactions.length === 0) && (
                      <tr>
                        <td colSpan={3} className="muted">Nenhuma transação encontrada.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminPelada;
